import React from 'react'
import styled, { StyledProps } from 'styled-components'
import { FormField } from '../../form'

type SwitchSize = 'small' | 'default'

type SwitchProps = StyledProps<{
  isChecked: boolean
  isWarning?: boolean
  size: SwitchSize
}>

const pickColor = (props: SwitchProps) => {
  if (props.isWarning) {
    return props.theme.color.accentDestructive
  }
  return props.isChecked ? props.theme.color.accentApproving : props.theme.color.secondary200
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${pickColor};
`

const ButtonStyled = styled.button<SwitchProps>`
  border-width: 1px;
  border-style: solid;
  border-radius: 12px;
  background: none;
  outline: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: 0.1s background-color ease-out, 0.1s border-color ease-out;
  margin-right: 8px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${(props: SwitchProps) => {
    const color = pickColor(props)
    let width
    let height
    switch (props.size) {
      case 'default':
        width = '44px'
        height = '24px'
        break
      case 'small':
        width = '36px'
        height = '20px'
    }
    return {
      borderColor: color,
      backgroundColor: `${color}14`,
      width,
      height,
    }
  }};
`

const SliderStyled = styled.span<SwitchProps>`
  position: absolute;
  border-radius: 50%;
  transition: 0.1s background-color ease-out, 0.1s transform ease-out;

  ${(props: SwitchProps) => {
    const color = pickColor(props)
    let offset
    let size
    switch (props.size) {
      case 'default':
        size = '16px'
        offset = props.isChecked ? 20 : 5
        break
      case 'small':
        size = '14px'
        offset = props.isChecked ? 17 : 3
    }
    return {
      backgroundColor: color,
      transform: `translateX(${offset}px)`,
      width: size,
      height: size,
    }
  }};
`

const FormFieldStyled = styled(FormField)`
  max-width: max-content;
`

export const Switch: React.FC<{
  on: boolean
  onClick(value: boolean): void
  disabled?: boolean
  warning?: boolean
  label?: string
  id?: string
  className?: string
  size?: SwitchSize
  name?: string
  testId?: string
}> = ({ on, onClick, disabled = false, warning, label, id, className, children, name, size = 'default', testId }) => {
  return (
    <FormFieldStyled
      id={id}
      className={className}
      label={label}
      name={name}
      testId={testId}
      render={({ id, name }) => (
        <Wrapper isChecked={on} isWarning={warning} size={size}>
          <ButtonStyled
            id={id}
            name={name}
            disabled={disabled}
            isChecked={on}
            isWarning={warning}
            type={'button'}
            role={'switch'}
            aria-checked={on}
            onClick={disabled ? undefined : () => onClick(!on)}
            size={size}
          >
            <SliderStyled isChecked={on} isWarning={warning} size={size} />
          </ButtonStyled>
          {children}
        </Wrapper>
      )}
    />
  )
}
